import styled from '@emotion/styled';
import { HAIRSP } from '@racemap/utilities/consts/characters';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { Button, Flex, Form, Modal, Radio, Select, Slider, Switch } from 'antd';
import { type FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useStore } from '../../store/reducers';
import type { ShippingInfo } from '../../store/trackers/trackers_reducers';
import { IconShipping } from '../Icon';
import { MessageType, TimePeriod } from './constants';

export const ShippingButton: FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  const showModal = () => {
    setIsModalOpen(true);
  };
  const { setShippingInfo, setShowShippingCol } = useStore((state) => state.trackers.actions);
  const { shippingInfo, showShippingCol } = useStore((state) => state.trackers);

  useEffect(() => {
    if (shippingInfo) {
      form.setFieldsValue({
        lastConnectedAtTimePeriod: shippingInfo.lastConnectedAtTimePeriod,
        lastDataAtTimePeriod: shippingInfo.lastDataAtTimePeriod,
        minBattery: shippingInfo.minBattery,
        invalidMessageTypes: shippingInfo.invalidMessageTypes,
      });
    }
  }, [shippingInfo, form]);

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleSubmit = (values: ShippingInfo) => {
    toast.success('Shipping info updated');
    setShippingInfo(values);
    setIsModalOpen(false);
  };

  const handleFinishFailed = () => {
    toast.error('Failed to update shipping info');
  };

  const formatter = (value: number | undefined) => `${value}${HAIRSP}%`;

  const messageTypeOptions = [
    { value: MessageType.Error, label: 'Error' },
    { value: MessageType.Scheduled, label: 'Scheduled' },
    { value: MessageType.Pending, label: 'Pending' },
  ];

  return (
    <>
      <StyledIcon showShippingCol={showShippingCol} onClick={showModal} fixedWidth />
      <Modal
        title="Shipping"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" type="default" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={() => form.submit()}>
            Submit
          </Button>,
        ]}
      >
        <ShippingSwitch>
          <span>Activate Shipping:</span>
          <Switch value={showShippingCol} onChange={() => setShowShippingCol(!showShippingCol)} />
        </ShippingSwitch>
        <Form
          form={form}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 11 }}
          autoComplete="off"
          onFinish={handleSubmit}
          onFinishFailed={handleFinishFailed}
          disabled={!showShippingCol}
        >
          <Form.Item
            name="lastConnectedAtTimePeriod"
            label="Connected in last"
            rules={[{ required: true, message: 'Please select a last connection time!' }]}
          >
            <TimeRadioGroup>
              <Radio.Button value={TimePeriod.lastOneHour}>60 min</Radio.Button>
              <Radio.Button value={TimePeriod.lastTenMinutes}>10 min</Radio.Button>
              <Radio.Button value={TimePeriod.lastFiveMinutes}>5 min</Radio.Button>
            </TimeRadioGroup>
          </Form.Item>
          <Form.Item
            name="lastDataAtTimePeriod"
            label="Location Fix in last"
            rules={[{ required: true, message: 'Please select a location fix time!' }]}
          >
            <TimeRadioGroup>
              <Radio.Button value={TimePeriod.lastOneHour}>60 min</Radio.Button>
              <Radio.Button value={TimePeriod.lastTenMinutes}>10 min</Radio.Button>
              <Radio.Button value={TimePeriod.lastFiveMinutes}>5 min</Radio.Button>
            </TimeRadioGroup>
          </Form.Item>

          <Form.Item name="minBattery" label="Minimal Battery">
            <Slider tooltip={{ formatter }} marks={{ 0: '0%', 50: '50%', 100: '100%' }} />
          </Form.Item>
          <Form.Item name="invalidMessageTypes" label="Invalid messages">
            <MessageTypeSelect
              options={messageTypeOptions}
              mode="tags"
              placeholder="Message types"
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

const StyledIcon = styled(IconShipping)<{ showShippingCol: boolean }>`
  height: 15px;
  padding-right: 8px;
  color: ${({ showShippingCol }) =>
    !showShippingCol ? `${RacemapColors.LightGray} !important` : 'unset'};
`;

const TimeRadioGroup = styled(Radio.Group)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MessageTypeSelect = styled(Select)`
  width: 100%;
`;

const ShippingSwitch = styled(Flex)`
  flex-direction: row;
  width: 355px;
  padding: 20px;
  margin: 20px;
  border: 2px solid ${RacemapColors.LightGray}; 
  border-radius: 10px;
  background-color: ${RacemapColors.LightBlue}; 
  gap: 15px;
  justify-content: center;
`;
